// Only realApi and export remain
import { apiUrl } from './client';
import { draftsApi } from './drafts';

let authToken = '';
const readStoredToken = () => {
  const sessionToken = sessionStorage.getItem('token') || '';
  const persistentToken = localStorage.getItem('token_persistent') || '';
  const legacyToken = localStorage.getItem('token') || '';
  return sessionToken || persistentToken || legacyToken || '';
};

export function getAuthHeaders() {
  const token = authToken || readStoredToken();
  if (token && token !== authToken) {
    authToken = token;
  }
  const activeGroupId = sessionStorage.getItem('active_group_id') || '';
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  if (activeGroupId && Number.isFinite(Number(activeGroupId))) {
    headers['X-Active-Group-Id'] = String(activeGroupId);
  }
  return headers;
}

export const setApiAuthToken = token => {
  authToken = typeof token === 'string' ? token : '';
};

const normalizeUserPayload = user => {
  if (!user) return null;
  return {
    ...user,
    mustChangePassword:
      user.mustChangePassword ??
      user.must_change_password ??
      false,
    onboardingCompleted:
      user.onboardingCompleted ?? user.onboarding_completed ?? false,
    role: user.role || user.Role,
    group: user.group || user.Group,
  };
};

const splitTemplatesByVisibility = templates => {
  const validTemplates = (templates || []).filter(
    t => t.id !== null && t.id !== 'null' && t.id !== 'none'
  );

  const global = validTemplates.filter(
    t =>
      t.visibility === 'global' ||
      (typeof t.visibility === 'string' && t.visibility.startsWith('group-'))
  );

  // Keep all non-global/non-group templates visible, including legacy rows
  // where visibility might be null/empty.
  const user = validTemplates.filter(
    t =>
      t.visibility !== 'global' &&
      !(typeof t.visibility === 'string' && t.visibility.startsWith('group-'))
  );

  return { global, user };
};

const realApi = {
  async login(username, password, rememberMe = false) {
    const res = await fetch(apiUrl('auth/login'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password, rememberMe: Boolean(rememberMe) }),
    });
    if (!res.ok) {
      let message = 'Login failed';
      try {
        const data = await res.json();
        message = data.error || message;
      } catch {}
      return { success: false, message };
    }
    const data = await res.json();
    return { success: true, user: data.user, token: data.token || '' };
  },
  async logout() {
    await fetch(apiUrl('auth/logout'), {
      method: 'POST',
    });
  },
  async changePassword(userId, newPassword, oldPassword) {
    const res = await fetch(apiUrl(`users/${userId}`), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify({
        password: newPassword,
        oldPassword,
        must_change_password: false,
      }),
    });
    if (!res.ok) {
      let message = 'Failed to change password';
      try {
        const data = await res.json();
        message = data.error || message;
      } catch {}
      return { success: false, message };
    }
    const data = await res.json();
    return {
      success: true,
      user: normalizeUserPayload(data),
    };
  },
  async getTemplates(user) {
    const res = await fetch(apiUrl('templates'), {
      headers: { ...getAuthHeaders() },
    });
    if (!res.ok) throw new Error('Failed to fetch templates');
    const templates = await res.json();

    return splitTemplatesByVisibility(templates);
  },
  async getEvents() {
    const res = await fetch(apiUrl('events'), {
      headers: { ...getAuthHeaders() },
    });
    if (!res.ok) throw new Error('Failed to fetch events');
    return res.json();
  },
  async createEvent({ name, visibility = 'public', group_ids = [] }) {
    const res = await fetch(apiUrl('events'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify({ name, visibility, group_ids }),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      return {
        success: false,
        message: data.error || 'Failed to create event',
      };
    }
    return { success: true, event: await res.json() };
  },
  async updateEvent(id, payload) {
    const res = await fetch(apiUrl(`events/${id}`), {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      return {
        success: false,
        message: data.error || 'Failed to update event',
      };
    }
    return { success: true, event: await res.json() };
  },
  async deleteEvent(id) {
    const res = await fetch(apiUrl(`events/${id}`), {
      method: 'DELETE',
      headers: { ...getAuthHeaders() },
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      return {
        success: false,
        message: data.error || 'Failed to delete event',
      };
    }
    return { success: true };
  },
  async getEventTemplates(eventId) {
    const res = await fetch(apiUrl(`events/${eventId}/templates`), { headers: { ...getAuthHeaders() } });
    if (!res.ok) throw new Error('Failed to fetch event templates');
    return res.json();
  },
  async listEventVariables({ scope = 'global', groupId = null, eventId = null } = {}) {
    let path = 'global';
    if (scope === 'group' && groupId) {
      path = `group-${groupId}`;
    } else if (scope === 'event' && eventId) {
      path = `event-${eventId}`;
    }
    const res = await fetch(apiUrl(`event-variables/${path}`), {
      headers: { ...getAuthHeaders() },
    });
    if (!res.ok) throw new Error('Failed to fetch event variables');
    return res.json();
  },
  async createEventVariable({ scope = 'global', groupId = null, eventId = null } = {}, payload) {
    const path = scope === 'group' && groupId ? `group-${groupId}` : scope === 'event' && eventId ? `event-${eventId}` : 'global';
    const body = {
      ...payload,
      scope,
      group_id: scope === 'group' ? groupId : null,
      event_id: scope === 'event' ? eventId : null,
    };
    const res = await fetch(apiUrl(`event-variables/${path}`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || 'Failed to create variable');
    }
    return res.json();
  },
  async updateEventVariable(id, payload) {
    const res = await fetch(apiUrl(`event-variables/item/${id}`), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || 'Failed to update variable');
    }
    return res.json();
  },
  async listAccessibleGroups() {
    const res = await fetch(apiUrl('groups/accessible'), {
      headers: { ...getAuthHeaders() },
    });
    if (!res.ok) throw new Error('Failed to fetch accessible groups');
    return res.json();
  },
  async deleteEventVariable(id) {
    const res = await fetch(apiUrl(`event-variables/item/${id}`), {
      method: 'DELETE',
      headers: { ...getAuthHeaders() },
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || 'Failed to delete variable');
    }
    return res.json();
  },
  async listLanguageKeys() {
    const res = await fetch(apiUrl('language-keys'), {
      headers: { ...getAuthHeaders() },
    });
    if (!res.ok) throw new Error('Failed to fetch languages');
    return res.json();
  },
  async createLanguageKey(payload) {
    const res = await fetch(apiUrl('language-keys'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || 'Failed to create language');
    }
    return res.json();
  },
  async updateLanguageKey(id, payload) {
    if (!id) throw new Error('Language key ID required');
    const res = await fetch(apiUrl(`language-keys/${id}`), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || 'Failed to update language key');
    }
    return res.json();
  },
  async listTextTemplates(params = {}) {
    const query = new URLSearchParams();
    if (params.eventId) query.set('eventId', params.eventId);
    if (params.role) query.set('role', params.role);
    const res = await fetch(apiUrl(`text-templates${query.toString() ? `?${query.toString()}` : ''}`), {
      headers: { ...getAuthHeaders() },
    });
    if (!res.ok) throw new Error('Failed to fetch text templates');
    return res.json();
  },
  async getTextTemplate(id) {
    if (!id) throw new Error('Text template id required');
    const res = await fetch(apiUrl(`text-templates/${id}`), {
      headers: { ...getAuthHeaders() },
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || 'Failed to fetch text template');
    }
    return res.json();
  },
  async createTextTemplate(payload) {
    const res = await fetch(apiUrl('text-templates'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || 'Failed to create template');
    }
    return res.json();
  },
  async updateTextTemplate(id, payload) {
    const res = await fetch(apiUrl(`text-templates/${id}`), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || 'Failed to update template');
    }
    return res.json();
  },
  async deleteTextTemplate(id) {
    const res = await fetch(apiUrl(`text-templates/${id}`), {
      method: 'DELETE',
      headers: { ...getAuthHeaders() },
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || 'Failed to delete template');
    }
    return res.json();
  },
  async duplicateTextTemplate(id) {
    const res = await fetch(apiUrl(`text-templates/${id}/duplicate`), {
      method: 'POST',
      headers: { ...getAuthHeaders() },
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || 'Failed to duplicate template');
    }
    return res.json();
  },
  async batchGenerateTextTemplates(payload) {
    const res = await fetch(apiUrl('text-templates/batch-generate'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || 'Failed to generate templates');
    }
    return res.json();
  },
  async duplicateTemplate(templateId, target_event_id, name) {
    const res = await fetch(apiUrl(`templates/${templateId}/duplicate`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify({ target_event_id, name }),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      return {
        success: false,
        message: data.error || 'Failed to duplicate template',
      };
    }
    return { success: true, ...(await res.json()) };
  },
  async bulkCopyTemplates(ids, target_event_id) {
    const res = await fetch(apiUrl('templates/bulk-copy'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify({ ids, target_event_id }),
    });
    if (!res.ok) throw new Error('Failed to bulk copy');
    return res.json();
  },
  async bulkMoveTemplates(ids, target_event_id) {
    const res = await fetch(apiUrl('templates/bulk-move'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify({ ids, target_event_id }),
    });
    if (!res.ok) throw new Error('Failed to bulk move');
    return res.json();
  },
  async bulkDeleteTemplates(ids) {
    const res = await fetch(apiUrl('templates/bulk-delete'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify({ ids }),
    });
    if (!res.ok) {
      let msg = 'Failed to bulk delete';
      try {
        const d = await res.json();
        msg = d.error || msg;
      } catch {}
      throw new Error(msg);
    }
    return res.json();
  },

  async getDeletedTemplates() {
    const res = await fetch(apiUrl('templates/deleted'), {
      headers: { ...getAuthHeaders() },
    });
    if (!res.ok) throw new Error('Failed to fetch deleted templates');
    return res.json();
  },
  async restoreTemplate(id) {
    const res = await fetch(apiUrl(`templates/${id}/restore`), {
      method: 'POST',
      headers: { ...getAuthHeaders() },
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      return {
        success: false,
        message: data.error || 'Failed to restore template',
      };
    }
    return { success: true };
  },
  async getAllTemplatesForAdmin() {
    const res = await fetch(apiUrl('templates'), {
      headers: { ...getAuthHeaders() },
    });
    if (!res.ok) {
      let message = 'Failed to fetch templates';
      try {
        const data = await res.json();
        message = data.error || message;
      } catch {}
      throw new Error(message);
    }
    const templates = await res.json();

    return splitTemplatesByVisibility(templates);
  },
  async getTemplatesByEvent(eventId) {
    if (!eventId) {
      // Fallback to all templates
      return this.getTemplates();
    }
    const res = await fetch(apiUrl(`events/${eventId}/templates`), {
      headers: { ...getAuthHeaders() },
    });
    if (!res.ok) {
      let message = 'Failed to fetch templates';
      try {
        const data = await res.json();
        message = data.error || message;
      } catch {}
      throw new Error(message);
    }
    const { templates } = await res.json();
    return splitTemplatesByVisibility(templates);
  },
  async saveTemplate(userId, templateData) {
    // POST to /api/templates (create) or PUT to /api/templates/:id (update)
    if (
      templateData.id &&
      templateData.id !== null &&
      templateData.id !== 'null' &&
      templateData.id !== 'none'
    ) {
      // Update existing template
      const res = await fetch(apiUrl(`templates/${templateData.id}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(templateData),
      });
      if (!res.ok) {
        let message = 'Failed to update template';
        try {
          const data = await res.json();
          message = data.error || message;
        } catch {}
        return { success: false, message };
      }
      return { success: true };
    } else {
      // Create new template
      const res = await fetch(apiUrl('templates'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(templateData),
      });

      if (!res.ok) {
        let message = 'Failed to create template';
        try {
          const data = await res.json();
          message = data.error || message;
        } catch {}
        return { success: false, message };
      }

      try {
        const result = await res.json();
        return result;
      } catch (e) {
        return { success: true, message: 'Template saved successfully' };
      }
    }
  },
  async getTemplateVersions(templateId) {
    if (!templateId) return [];
    const res = await fetch(apiUrl(`templates/${templateId}/versions`), {
      headers: { ...getAuthHeaders() },
    });
    if (!res.ok) {
      let message = 'Failed to load template versions';
      try {
        const data = await res.json();
        message = data.error || message;
      } catch {}
      throw new Error(message);
    }
    return res.json();
  },
  async createTemplateVersion(templateId, payload) {
    const res = await fetch(apiUrl(`templates/${templateId}/versions`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      let message = 'Failed to create snapshot';
      try {
        const data = await res.json();
        message = data.error || message;
      } catch {}
      throw new Error(message);
    }
    return res.json();
  },
  async updateTemplateSource(templateId, payload) {
    if (!templateId) {
      throw new Error('Template ID required to update source.');
    }
    const res = await fetch(apiUrl(`templates/${templateId}/source`), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      let message = 'Failed to update template source';
      try {
        const data = await res.json();
        message = data.error || message;
      } catch {}
      throw new Error(message);
    }
    return res.json();
  },
  async deleteUserTemplate(userId, templateId) {
    // Only allow delete if owner matches (enforced on backend ideally)

    // Validate template ID
    if (
      !templateId ||
      templateId === null ||
      templateId === 'null' ||
      templateId === 'none'
    ) {
      return { success: false, message: 'Invalid template ID' };
    }

    const headers = getAuthHeaders();
    const res = await fetch(apiUrl(`templates/${templateId}`), {
      method: 'DELETE',
      headers,
    });
    if (!res.ok) {
      let message = 'Failed to delete template';
      try {
        const data = await res.json();
        message = data.error || message;
      } catch {}
      return { success: false, message };
    }
    return { success: true };
  },
  async saveGlobalTemplate(templateData) {
    // POST to /api/templates (create)
    // Don't override visibility if it's already set (for group templates)
    const visibility = templateData.visibility || 'global';
    const res = await fetch(apiUrl('templates'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify({ ...templateData, visibility }),
    });
    if (!res.ok) {
      let message = 'Failed to create global template';
      try {
        const data = await res.json();
        message = data.error || message;
      } catch {}
      return { success: false, message };
    }
    return { success: true };
  },

  async saveGroupTemplate(templateData) {
    // POST to /api/templates (create) for group templates
    // This allows regular users to share templates with their groups
    const res = await fetch(apiUrl('templates'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(templateData),
    });

    if (!res.ok) {
      let message = 'Failed to create group template';
      try {
        const data = await res.json();
        message = data.error || message;
      } catch {}
      return { success: false, message };
    }

    try {
      const result = await res.json();
      return result;
    } catch (e) {
      return { success: true, message: 'Group template saved successfully' };
    }
  },
  async updateGlobalTemplate(templateData) {
    // PUT to /api/templates/:id
    // Don't override visibility if it's already set (for group templates)
    const visibility = templateData.visibility || 'global';

    // Validate template ID
    if (
      !templateData.id ||
      templateData.id === null ||
      templateData.id === 'null' ||
      templateData.id === 'none'
    ) {
      return { success: false, message: 'Invalid template ID' };
    }

    // Ensure we preserve all template data by including the full templateData object
    const updateData = { ...templateData, visibility };

    const res = await fetch(apiUrl(`templates/${templateData.id}`), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(updateData),
    });
    if (!res.ok) {
      let message = 'Failed to update template';
      try {
        const data = await res.json();
        message = data.error || message;
      } catch {}
      return { success: false, message };
    }
    return { success: true };
  },

  async updateUserTemplate(templateData) {
    // PUT to /api/templates/:id (same endpoint, different permission handling)

    // Validate template ID
    if (
      !templateData.id ||
      templateData.id === null ||
      templateData.id === 'null' ||
      templateData.id === 'none'
    ) {
      return { success: false, message: 'Invalid template ID' };
    }

    const res = await fetch(apiUrl(`templates/${templateData.id}`), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(templateData),
    });
    if (!res.ok) {
      let message = 'Failed to update user template';
      try {
        const data = await res.json();
        message = data.error || message;
      } catch {}
      return { success: false, message };
    }
    return { success: true };
  },
  async deleteGlobalTemplate(templateId) {
    // DELETE to /api/templates/:id

    // Validate template ID
    if (
      !templateId ||
      templateId === null ||
      templateId === 'null' ||
      templateId === 'none'
    ) {
      return { success: false, message: 'Invalid template ID' };
    }

    const headers = getAuthHeaders();
    const res = await fetch(apiUrl(`templates/${templateId}`), {
      method: 'DELETE',
      headers,
    });
    if (!res.ok) {
      let message = 'Failed to delete global template';
      try {
        const data = await res.json();
        message = data.error || message;
      } catch {}
      return { success: false, message };
    }
    return { success: true };
  },
  async getUsers() {
    const res = await fetch(apiUrl('users'), {
      headers: { ...getAuthHeaders() },
    });
    if (!res.ok) throw new Error('Failed to fetch users');
    const users = await res.json();
    return users.map(user => ({
      ...user,
      role: user.Role ? user.Role.name : 'N/A',
      group: user.Group ? user.Group.name : 'N/A',
      groups: Array.isArray(user.Groups) ? user.Groups : [],
      mustChangePassword: user.must_change_password,
    }));
  },
  async createUser(username, role_id, group_id, group_ids = []) {
    // POST to /api/users
    const randomPassword = Math.random().toString(36).slice(-8);
    const res = await fetch(apiUrl('users'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify({
        username,
        password: randomPassword,
        role_id,
        group_id,
        group_ids,
        must_change_password: true,
      }),
    });
    if (!res.ok) {
      let message = 'Failed to create user';
      try {
        const data = await res.json();
        message = data.error || message;
      } catch {}
      return { success: false, message };
    }
    const newUser = await res.json();
    return { success: true, newUser: { ...newUser, password: randomPassword } };
  },
  async deleteUser(userId) {
    const res = await fetch(apiUrl(`users/${userId}`), {
      method: 'DELETE',
      headers: { ...getAuthHeaders() },
    });
    if (!res.ok) {
      let message = 'Failed to delete user';
      try {
        const data = await res.json();
        message = data.error || message;
      } catch {}
      return { success: false, message };
    }
    return { success: true };
  },
  async updateUser(userId, data) {
    // PUT to /api/users/:id
    const res = await fetch(apiUrl(`users/${userId}`), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      let message = 'Failed to update user';
      try {
        const responseData = await res.json();
        message = responseData.error || message;
      } catch {}
      return { success: false, message };
    }
    return { success: true };
  },
  async markOnboardingComplete(userId) {
    const res = await fetch(
      apiUrl(`users/${userId}/onboarding-complete`),
      {
        method: 'POST',
        headers: { ...getAuthHeaders() },
      }
    );
    if (!res.ok) {
      let message = 'Failed to mark onboarding complete';
      try {
        const data = await res.json();
        message = data.error || message;
      } catch {}
      return { success: false, message };
    }
    const payload = await res.json();
    return {
      success: true,
      user: normalizeUserPayload(payload.user),
    };
  },
  async resetOnboarding(userId) {
    const res = await fetch(apiUrl(`users/${userId}/onboarding-reset`), {
      method: 'POST',
      headers: { ...getAuthHeaders() },
    });
    if (!res.ok) {
      let message = 'Failed to reset onboarding';
      try {
        const data = await res.json();
        message = data.error || message;
      } catch {}
      return { success: false, message };
    }
    const data = await res.json();
    return {
      success: true,
      user: normalizeUserPayload(data.user),
    };
  },
  async resetUserPassword(userId) {
    // POST to /api/users/:id/reset-password
    const res = await fetch(apiUrl(`users/${userId}/reset-password`), {
      method: 'POST',
      headers: { ...getAuthHeaders() },
    });
    if (!res.ok) {
      let message = 'Failed to reset password';
      try {
        const data = await res.json();
        message = data.error || message;
      } catch {}
      return { success: false, message };
    }
    const { newPassword } = await res.json();
    return { success: true, newPassword };
  },
  async getRoles() {
    const res = await fetch(apiUrl('roles'), {
      headers: { ...getAuthHeaders() },
    });
    if (!res.ok) {
      let message = 'Failed to fetch roles';
      try {
        const data = await res.json();
        message = data.error || message;
      } catch {}
      throw new Error(message);
    }
    return res.json();
  },
  async saveRole(roleData) {
    // POST to /api/roles (create) or PUT to /api/roles/:id (update)
    if (roleData.id) {
      const res = await fetch(apiUrl(`roles/${roleData.id}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(roleData),
      });
      if (!res.ok) {
        let message = 'Failed to update role';
        try {
          const data = await res.json();
          message = data.error || message;
        } catch {}
        return { success: false, message };
      }
      return { success: true };
    } else {
      const res = await fetch(apiUrl('roles'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(roleData),
      });
      if (!res.ok) {
        let message = 'Failed to create role';
        try {
          const data = await res.json();
          message = data.error || message;
        } catch {}
        return { success: false, message };
      }
      return { success: true };
    }
  },
  async updateRole(roleData) {
    // PUT to /api/roles/:id
    const res = await fetch(apiUrl(`roles/${roleData.id}`), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(roleData),
    });
    if (!res.ok) {
      let message = 'Failed to update role';
      try {
        const data = await res.json();
        message = data.error || message;
      } catch {}
      return { success: false, message };
    }
    return { success: true };
  },
  async deleteRole(roleId) {
    // DELETE to /api/roles/:id
    const res = await fetch(apiUrl(`roles/${roleId}`), {
      method: 'DELETE',
      headers: { ...getAuthHeaders() },
    });
    if (!res.ok) {
      let message = 'Failed to delete role';
      try {
        const data = await res.json();
        message = data.error || message;
      } catch {}
      throw new Error(message);
    }
    return { success: true };
  },
  async getGroups() {
    const res = await fetch(apiUrl('groups'), {
      headers: { ...getAuthHeaders() },
    });
    if (!res.ok) {
      let message = 'Failed to fetch groups';
      try {
        const data = await res.json();
        message = data.error || message;
      } catch {}
      throw new Error(message);
    }
    return res.json();
  },
  async saveGroup(groupData) {
    // POST to /api/groups (create) or PUT to /api/groups/:id (update)
    if (groupData.id) {
      const res = await fetch(apiUrl(`groups/${groupData.id}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(groupData),
      });
      if (!res.ok) {
        let message = 'Failed to update group';
        try {
          const data = await res.json();
          message = data.error || message;
        } catch {}
        return { success: false, message };
      }
      return { success: true };
    } else {
      const res = await fetch(apiUrl('groups'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(groupData),
      });
      if (!res.ok) {
        let message = 'Failed to create group';
        try {
          const data = await res.json();
          message = data.error || message;
        } catch {}
        return { success: false, message };
      }
      return { success: true };
    }
  },
  async updateGroup(groupData) {
    // PUT to /api/groups/:id
    const res = await fetch(apiUrl(`groups/${groupData.id}`), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(groupData),
    });
    if (!res.ok) {
      let message = 'Failed to update group';
      try {
        const data = await res.json();
        message = data.error || message;
      } catch {}
      return { success: false, message };
    }
    return { success: true };
  },
  async deleteGroup(groupId) {
    // DELETE to /api/groups/:id
    const res = await fetch(apiUrl(`groups/${groupId}`), {
      method: 'DELETE',
      headers: { ...getAuthHeaders() },
    });
    if (!res.ok) {
      let message = 'Failed to delete group';
      try {
        const data = await res.json();
        message = data.error || message;
      } catch {}
      throw new Error(message);
    }
    return { success: true };
  },
  async getGlobalSettings() {
    const res = await fetch(apiUrl('globalSettings'), {
      headers: { ...getAuthHeaders() },
    });
    if (!res.ok) {
      let message = 'Failed to fetch global settings';
      let payload;
      try {
        payload = await res.json();
        message = payload.error || payload.details || message;
      } catch {
        // ignore json parse errors
      }
      const error = new Error(message);
      error.status = res.status;
      if (payload !== undefined) error.payload = payload;
      throw error;
    }
    return res.json();
  },
  async updateGlobalSettings(newSettings) {
    const res = await fetch(apiUrl('globalSettings'), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(newSettings),
    });
    if (!res.ok) {
      let message = 'Failed to update global settings';
      try { const data = await res.json(); message = data.error || data.details || message; } catch {}
      throw new Error(message);
    }
    return res.json();
  },

  async testAuth() {
    const headers = getAuthHeaders();
    const res = await fetch(apiUrl('auth/test'), {
      headers: { ...headers },
    });
    if (!res.ok) {
      const errorText = await res.text();
      console.error('Auth test failed:', res.status, errorText);
      throw new Error(`Failed to test auth: ${res.status} ${errorText}`);
    }
    return await res.json();
  },

  async testAuthPing() {
    const res = await fetch(apiUrl('auth/ping'));
    if (!res.ok) {
      const errorText = await res.text();
      console.error('Auth ping failed:', res.status, errorText);
      throw new Error(`Failed to ping auth: ${res.status} ${errorText}`);
    }
    return await res.json();
  },

  async updateRegmailTemplate(payload) {
    const res = await fetch(apiUrl('regmail'), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(payload || {}),
    });
    if (!res.ok) {
      let message = 'Failed to update regmail template';
      try { const data = await res.json(); message = data.error || message; } catch {}
      throw new Error(message);
    }
    return res.json();
  },
  async getRegmailDefaults() {
    const res = await fetch(apiUrl('regmail/defaults'), {
      headers: { ...getAuthHeaders() },
    });
    if (!res.ok) {
      let message = 'Failed to load Regmail defaults';
      try { const data = await res.json(); message = data.error || message; } catch {}
      throw new Error(message);
    }
    return res.json();
  },
  async restoreRegmailTemplate() {
    const res = await fetch(apiUrl('regmail/restore'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    });
    if (!res.ok) {
      let message = 'Failed to restore Regmail template';
      try { const data = await res.json(); message = data.error || message; } catch {}
      throw new Error(message);
    }
    return res.json();
  },
  async sendTestEmail({ to, subject, html }) {
    const res = await fetch(apiUrl('email/test'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify({ to, subject, html }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data.error || 'Failed to send test email');
    }
    return data;
  },
  async getConfiguratorDraft(params) {
    const { draft } = await draftsApi.getCurrent(params);
    return draft;
  },
  async saveConfiguratorDraft(payload) {
    const { draft } = await draftsApi.saveCurrent(payload);
    return draft;
  },
  async clearConfiguratorDraft(params) {
    return draftsApi.clearCurrent(params);
  },
};

export const api = realApi;
